// §VI · EVIDENCE (the quotes) — ported from the Claude Design canon
// `R10 - §VI - canon F5.html` (project dd7b95d4, data-canon="R10-§VI-F5"). Layout =
// styles/r10-vi.css (byte-checked by css-parity-vi.test.js).
//
// F4 reverses F3's visible absence states back to SILENT SKIP (D&R s414.d2, over s280):
// a stage without a quote gets NO card at all — the reason code lives in the data (telemetry)
// and leaves no trace on the sheet; a silent quote_n renders nothing (the section ends at the
// last stack card); N_quotes < 2 REMOVES the section and renumbers the document (§VII → §VI)
// with NO printable copy. F5 (D&R s882.d1, over s414.d1/s417.d2) removes the LAST printable
// absence state: the doc-level transport_failed artboard is gone. A judge transport failure now
// behaves EXACTLY like N < 2 — the run yields zero real quotes, the section is removed and §VII
// renumbers to §VI. The paper is a frozen snapshot; a "transcript missing" page must never
// become a permanent page of someone's Life Map. `out.transport` still rides in the engine data
// (run-level telemetry only, s311.d6) — it just never reaches the printed page. Content is the
// System 2 engine's output (selectQuotes → stack[] / quote_n / reason codes); the boards carry
// the dataset-run quote data verbatim. Detector / System 2 is not touched here.
//
// §VI is SELF-MANAGED like §II–§V: it renders its OWN artboard shells (<section class=
// "artboard evidence-page">), mounted WITHOUT the generic Artboard wrapper. Unlike §V, a
// single board can emit MORE THAN ONE section: the canon paginates the stack by MEASURED
// height, so N=15 splits across two sheets. That measurement can't run under SSR (or jsdom) —
// so the sheet split and the per-sheet fit tier are BAKED into the board's `sheets` (captured
// once from a real browser; see sectionVIBoards.js). Numbering + denom are deterministic and
// recomputed here (reason cards filtered out first — the silent skip).
//
// The section body is emitted through the same string builders CD used and mounted via
// dangerouslySetInnerHTML, so the DOM is byte-identical to the canon (svg-parity-vi). The
// outer <section> (its data-* + fit class) is the React host.

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV'];
const NUMW = { 0: 'zero', 1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five', 6: 'six', 7: 'seven', 8: 'eight', 9: 'nine', 10: 'ten', 11: 'eleven', 12: 'twelve', 13: 'thirteen', 14: 'fourteen', 15: 'fifteen' };
// T-поля визарда, шаг 3 (deck; бывш. s-id «step6») — метки без внутренних индексов (R5 · B-3)
// ACCEPTED-DEBT (s447): T4 «who was around» снят из контракта (s443.d1/s452.d1); движок его не
// эмитит. Ярлык оставлен ТОЛЬКО чтобы синтетические стресс-борды v06–v08 (sectionVIBoards FSEQ)
// оставались byte-identical замороженному CD-golden. Убрать вместе с FSEQ при переиздании CD.
const FIELD = { T1: 'about', T2: 'knew but did otherwise', T3: "didn't allow yourself", T4: 'who was around' };

// ---- КАПЫ · финальные числа канона F4, неизменны в F5 (замер на этом макете) ----
const CAP_STACK = 180;
const CAP_QN = 250;
const CAP_NAME = 64;      // имя этапа в штампе §VI (в §II — 80: там колонка шире)
const MIN_QUOTES = 2;     // порог раздела: только реальные цитаты
function cap(t, n) { t = String(t || ''); return t.length <= n ? { t, cut: false } : { t: t.slice(0, n - 1).replace(/[\s,;:—-]+$/, '') + '…', cut: true }; }
const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;');
// defensive lookups — no-op for canon (in-range stage, T1–T4 field). They keep off-canon input
// (a stage index past ROMAN, a null/unknown field) from leaking "undefined". Because canon
// values make these identity, svg-parity-vi stays byte-green. (qa-reviewer §VI pass.)
const roman = (i) => ROMAN[i] || '';
const fld = (f) => FIELD[f] || '—';

// ================= рендер · карточка стопки (только реальная цитата — тихий скип) =================
function stackItem(q, st, no, denom) {
  st = st || {};
  const nm = cap(st.name, CAP_NAME);
  const stamp = `<span class="ev-item-roman" data-field="quote_cards[j].stage_roman">${roman(q.stage)}</span>
    <span class="ev-item-stage" data-field="quote_cards[j].stage_name"${nm.cut ? ` data-cap-applied="name:${CAP_NAME}"` : ''}>${esc(nm.t)}</span>`;
  const tx = cap(q.text, CAP_STACK);
  return `<li class="ev-item" data-stage="${q.stage}">
<div class="ev-item-no" data-field="quote_cards[j].number_tag"><span class="ev-item-no-n">${String(no).padStart(2, '0')}</span><span class="ev-item-no-of">/ ${String(denom).padStart(2, '0')}</span></div>
<div class="ev-item-body">
  <div class="ev-item-stamp">${stamp}
    <span class="ev-item-field" data-field="quote_cards[j].field_tag">field · ${fld(q.field)}</span>
  </div>
  <blockquote class="ev-item-quote" data-field="quote_cards[j].quote_text"${tx.cut ? ` data-cap-applied="stack:${CAP_STACK}"` : ''}><span class="ev-q-open">&#8220;</span>${esc(tx.t)}<span class="ev-q-close">&#8221;</span></blockquote>
  <div class="ev-item-attr" data-field="quote_cards[j].attribution"><span class="ev-item-attr-bar"></span><em>— you, stage ${roman(q.stage)} · ${fld(q.field)}</em></div>
</div></li>`;
}
// ================= рендер · quote_n (крупный регистр — только реальная цитата) =================
function nowBlock(q, st, denom) {
  st = st || {};
  const nm = cap(st.name, CAP_NAME);
  const sep = `<div class="ev-sep" aria-hidden="true"><span class="ev-sep-mark">·&nbsp;&nbsp;·&nbsp;&nbsp;·</span></div>`;
  const tx = cap(q.text, CAP_QN);
  return `${sep}<article class="ev-now" data-field="quote_n_block">
<div class="ev-now-no"><span class="ev-now-no-n">${String(denom).padStart(2, '0')}</span><span class="ev-now-no-of">/ ${String(denom).padStart(2, '0')}</span><span class="ev-now-flag">now</span></div>
<div class="ev-now-body">
  <div class="ev-now-stamp">
    <span class="ev-now-roman">${roman(q.stage)}</span>
    <span class="ev-now-stage"${nm.cut ? ` data-cap-applied="name:${CAP_NAME}"` : ''}>${esc(nm.t)}</span>
    <span class="ev-now-field">field · ${fld(q.field)}</span>
  </div>
  <blockquote class="ev-now-quote" data-field="quote_n"${tx.cut ? ` data-cap-applied="quote_n:${CAP_QN}"` : ''}><span class="ev-q-open">&#8220;</span>${esc(tx.t)}<span class="ev-q-close">&#8221;</span></blockquote>
  <div class="ev-now-attr"><span class="ev-item-attr-bar"></span><em>— you, stage ${roman(q.stage)} · now · ${fld(q.field)}</em></div>
</div></article>`;
}
// ================= оболочка листа (inner of shell) =================
// everything INSIDE the <section> — the outer <section> (its data-* + fit class) is the React
// host; this returns its innerHTML, byte-identical to CD's shell(v, inner, ...).
function shellInner(inner) {
  return `
<div class="ab-paper"></div><div class="ab-grain"></div>
<header class="ab-runner">
  <span data-field="section_header_left"><span class="runner-roman">VI</span>evidence</span>
  <span class="runner-meta" data-field="section_header_right">life map · the reading</span>
</header>
${inner}
<footer class="ab-foot">
  <span data-field="footer_left"><span class="foot-em">life map</span> <span class="foot-faint">·</span> evidence <span class="foot-faint">·</span> <span class="foot-amber">verbatim</span></span>
  <span class="foot-page" data-field="footer_right">vi · transcript</span>
</footer>
`;
}
// один лист §VI: часть стопки + (на последнем листе) quote_n
function sheetInner(v, items, denom, offset, opts) {
  const N = v.stages.length, first = offset === 0;
  const q = opts.qn ? nowBlock(opts.qn.q, v.stages[opts.qn.q.stage], denom) : '';
  const shown = items.filter((x) => !x.reason).length;
  const head = first
    ? `<h2 class="ev-title" data-field="page_title"><em>your own words.</em> <span class="ev-em">${NUMW[denom] || denom} of them, in order.</span></h2>`
    : `<h2 class="ev-title" data-field="page_title"><em>your own words,</em> <span class="ev-em">continued.</span></h2>`;
  const intro = first
    ? `<p class="ev-sub" data-field="intro_paragraph">the lines and the patterns are inferences. <em>these are verbatim.</em> your own phrasing, returned without edit. the shape of the document above lives inside these sentences — the document trusts them more than its own prose.</p>
<div class="ev-side">
  <div class="ev-side-k" data-field="quotes_sidebar.label">quotes</div>
  <p class="ev-side-dx" data-field="quotes_sidebar.descriptor"><em>${NUMW[denom] || denom} quotes</em> out of the open-field responses. trimmed for length only; never reworded. names removed where they appear. selected by coverage — every stage that spoke gets one voice. the order on this page is the order of your life.</p>
</div>`
    : `<p class="ev-sub ev-cont-note" data-field="sheet_continuation">stack continued — quotes ${String(offset + 1).padStart(2, '0')}–${String(offset + shown).padStart(2, '0')} of ${String(denom).padStart(2, '0')}.</p>`;
  return `<div class="ev-sheet">
  <div class="ev-hd">
    <div class="ev-roman">VI</div>
    ${head}
    <div class="ev-meta" data-field="title_side_meta"><span class="ev-meta-count"><b>${denom}</b><span>quotes</span><span class="sep">·</span><span class="num">${N}</span><span>stages</span></span><em>not interpretation. transcription.</em></div>
  </div>
  ${intro}
  <ol class="ev-list">${items.map((x) => stackItem(x, v.stages[x.stage], x._no, denom)).join('')}</ol>
  ${q}
</div>`;
}
// N_quotes < 2 → раздел УДАЛЯЕТСЯ, §VII перенумеровывается в §VI · печатаемой копи НЕТ.
// Служебный спека-борд (заголовок sans .ev-absent-h-spec, без data-field): показывает дельту
// перенумерации, а не страницу. НЕ зеркалить §V-шный печатаемый section_absent (s414.d2).
function absentInner() {
  return `<div class="ev-sheet"><div class="ev-absent ev-absent-spec">
  <div class="ev-absent-k">§VI · спека состояния · страницы не существует</div>
  <div class="ev-absent-h ev-absent-h-spec">Реальных цитат меньше <b>${MIN_QUOTES}</b> — раздел <b>удаляется</b>, документ перенумеровывается. Печатаемой копи у этого состояния <b>нет</b>: пользователь видит не «пустой §VI», а его отсутствие.</div>
  <div class="ev-absent-list">
    <div class="ev-absent-row"><div class="ev-absent-f">§VI · весь раздел</div><div class="ev-absent-v"><del>evidence</del><ins>удалён</ins><span>· ни листа, ни строки</span></div></div>
    <div class="ev-absent-row"><div class="ev-absent-f">§VII · section_header_left</div><div class="ev-absent-v"><del>vii closing</del><ins>vi closing</ins></div></div>
    <div class="ev-absent-row"><div class="ev-absent-f">§VII · footer_right</div><div class="ev-absent-v"><del>vii · closing</del><ins>vi · closing</ins></div></div>
    <div class="ev-absent-row"><div class="ev-absent-f">footerCount</div><div class="ev-absent-v"><ins>0</ins></div></div>
    <div class="ev-absent-row"><div class="ev-absent-f">печатаемая копи</div><div class="ev-absent-v"><ins>нет</ins><span>· печатаемых строк отсутствия в §VI нет ни в одном состоянии · s882.d1</span></div></div>
  </div>
  <p class="ev-absent-note">порог §VI — <b>${MIN_QUOTES}</b> реальные цитаты; per-stage <code>not_evaluated</code> и любой другой reason в счёт не идут. Правило — <b>удаление + сдвиг номера</b> (s280 · s330 · s311.d3 · s325.d1), не печать состояния: §V-шный <code>section_absent</code> сюда не зеркалится. Артборд существует только как спека для порта; в продукте страницы нет.</p>
</div></div>`;
}
// F5 (s882.d1): the doc-level transport_failed artboard is GONE. A judge transport failure is
// no longer a printable §VI state — the run yields zero real quotes, so it falls through the
// same `< MIN_QUOTES` branch above (section removed, §VII → §VI). transportInner() was removed
// here; the history lives in the F4 canon file.

// ================= сборка секций для борда =================
// Numbering + denom are deterministic: reason cards are dropped BEFORE layout (silent skip),
// then numbered contiguously; the sheet split + fit tier come from the board's baked `sheets`.
function sectionsFor(v) {
  const base = { screen: v.screen, source: v.source, stageCount: v.stages.length };
  // silent skip: reason slots are dropped before layout — no card, no line, no trace.
  // (F5/s882.d1: no transport branch — a transport failure yields zero real quotes and takes
  // the `< MIN_QUOTES` path below, same as an empty run.)
  const items = v.stack.filter((o) => !o.reason).map((o) => ({ ...o }));
  let n = 0; items.forEach((x) => { x._no = ++n; });
  const qnReal = v.qn && !v.qn.reason;
  const denom = n + (qnReal ? 1 : 0);
  if (denom < MIN_QUOTES) {
    return [{ ...base, label: `${v.screen} · раздел удалён · §VII → §VI`, cls: 'artboard evidence-page',
      degradation: 'section_absent', quoteCount: '0', fit: null, inner: shellInner(absentInner()) }];
  }
  const out = [];
  let cursor = 0;
  v.sheets.forEach((s, i) => {
    const last = i === v.sheets.length - 1;
    const chunk = items.slice(cursor, cursor + s.take);
    const offset = cursor;
    cursor += s.take;
    // DoD #2: a silent (reason) quote_n does NOT render — the section ends at the last stack
    // card. Guarded by qnReal, not just qn's presence. No canon board feeds a reason-qn to a
    // sheet (all sheet boards have a real qn; v04 goes absent), so this is byte-identical to
    // the canon on every parity board while implementing the production branch correctly.
    const opts = { qn: last && qnReal ? { q: v.qn } : null };
    out.push({
      ...base,
      label: `${v.screen}${offset === 0 ? '' : ' · лист 2'}`,
      cls: 'artboard evidence-page' + (s.cls ? ' ' + s.cls : ''),
      degradation: null,
      quoteCount: String(denom),
      fit: s.note,
      inner: shellInner(sheetInner(v, chunk, denom, offset, opts)),
    });
  });
  return out;
}

export default function SectionVI({ view }) {
  const sections = sectionsFor(view);
  return sections.map((s, i) => (
    <section
      key={i}
      className={s.cls}
      data-screen-label={s.label}
      data-run={s.source}
      data-canon="R10-§VI-F5"
      data-stage-count={s.stageCount}
      data-quote-count={s.quoteCount}
      {...(s.degradation ? { 'data-degradation': s.degradation } : {})}
      {...(s.fit != null ? { 'data-fit': s.fit } : {})}
      data-overflow-px=""
      dangerouslySetInnerHTML={{ __html: s.inner }}
    />
  ));
}
